import React, { Component } from 'react';
import { arrayOf, func, string, shape, oneOf, any, bool } from 'prop-types';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import { AccessTime } from '@material-ui/icons';
import FilterableTimeZoneList from './FilterableTimezoneList';
import { timeZoneType, popularTimeZoneNames, NOT_FOUND } from './constants';
import { cloneDeep, isEqual, get, orderBy } from 'lodash';
import { Tooltip, withStyles } from '@material-ui/core/index';
import { createTheme, MuiThemeProvider } from '@material-ui/core/index';

import styles from './styles.scss';

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 550
    }
  },
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left'
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left'
  },
  getContentAnchorEl: null
};
const muiStyles = {
  customTooltip: {
    textTransform: 'capitalize'
  }
};

export const customMenuItem = createTheme({
  overrides: {
    MuiList: {
      padding: {
        paddingRight: '0px!important'
      },
      root: {
        width: 'calc(100% + 0px)!important'
      }
    },
  }
})

export class TimeZoneSelector extends Component {
  static propTypes = {
    displayIcon: bool,
    timeZones: arrayOf(timeZoneType),
    popularTimeZones: arrayOf(timeZoneType),
    selectedTimeZone: shape({
      name: string,
      timezoneKey: string
    }),
    onChange: func,
    loadTimeZones: func,
    timeZoneLoadStatus: oneOf(Object.values(timeZoneStatus)),
    customTimezones: arrayOf(timeZoneType),
    classes: shape({ any }),
    disabled: bool,
    isUsingOutline: bool,
    customStyle: string,
  };

  static defaultProps = {
    displayIcon: true,
    timeZones: [],
    popularTimeZones: [],
    customTimezones: [],
    selectedTimeZone: {},
    isUsingOutline: false,
  };

  state = {
    anchorEl: null,
    displayTimeZoneInfo: false
  };

  static getDerivedStateFromProps(props, prevState) {
    const { selectedTimeZone } = props;
    const newState = cloneDeep(prevState);
    if (!isEqual(selectedTimeZone, prevState.prevSelectedTimeZone)) {
      newState.selectedTimeZone = selectedTimeZone;
      newState.prevSelectedTimeZone = cloneDeep(selectedTimeZone);
    }
    return newState;
  }

  componentDidMount() {
    const {
      timeZoneLoadStatus,
      loadTimeZones
    } = this.props;

    if (
      timeZoneLoadStatus &&
      timeZoneLoadStatus !== timeZoneStatus.LOADED &&
      timeZoneLoadStatus !== timeZoneStatus.LOADING
    ) {
      loadTimeZones && loadTimeZones();
    }
  }

  handleSelect = (value) => {
    const { onChange } = this.props;
    const { name, timezoneKey } = value;
    const selectedTimeZone = { name, timezoneKey };

    this.handleClose();
    this.setState({ selectedTimeZone });
    onChange && onChange(selectedTimeZone);
  };

  handleClose = () => {
    this.setState({ anchorEl: null });
  };

  handleOpen = (event) => {
    this.setState({ anchorEl: event.currentTarget });
  };

  handleMouseEnter = () => {
    this.setState({ displayTimeZoneInfo: true });
  };

  handleMouseLeave = () => {
    this.setState({ displayTimeZoneInfo: false });
  };

  get selectedTimeZone() {
    const { timeZones, customTimezones } = this.props;
    const { selectedTimeZone } = this.state;
    const { name, timezoneKey } = selectedTimeZone;
    const emptyTimeZone = { abbv: NOT_FOUND };
    if (customTimezones.length > 0) {
      const allTimeZones = [
        ...customTimezones,
        ...timeZones
      ]
      return allTimeZones.find(timezone => timezone.timezoneKey === timezoneKey && timezone.name === name) || emptyTimeZone;
    }
    return timeZones.find(timezone => timezone.timezoneKey === timezoneKey && timezone.name === name) || emptyTimeZone;
  }

  //---------------------------------------------------------------------
  //---------------------------Draw Functions----------------------------
  //---------------------------------------------------------------------
  drawSearchDisplay() {
    const {
      timeZones,
      popularTimeZones,
      customTimezones,
      disabled,
      isUsingOutline,
      customStyle,
    } = this.props;
    const { selectedTimeZone, anchorEl } = this.state;

    return (
      <FormControl className={{[styles.searchDisplay]: !isUsingOutline}}>
        <Select
          variant={isUsingOutline ? 'outlined' : 'standard'}
          disabled={disabled}
          data-test="timezoneSelectBox"
          open={!!anchorEl}
          value={selectedTimeZone}
          MenuProps={MenuProps}
          renderValue={this.drawDropdownValue}
          onOpen={this.handleOpen}
          onClose={this.handleClose}
          className={customStyle}
        >
          {
            timeZones.length > 0 &&
            <FilterableTimeZoneList
              onSelect={this.handleSelect}
              selectedTimeZone={selectedTimeZone}
              popularTimeZones={popularTimeZones}
              timeZones={timeZones}
              customTimezones={customTimezones}
            />
          }
        </Select>
      </FormControl>
    );
  }

  drawDropdownValue = () => {
    const { abbv, name } = this.selectedTimeZone;

    return (
      <div data-test="timezoneSelected" className={styles.labelContainer}>
        <span data-test="timezoneSelectedName">{`${abbv || name}`}</span>
      </div>
    );
  };

  render() {
    const { classes, displayIcon } = this.props;
    const { displayTimeZoneInfo, anchorEl } = this.state;
    const { abbv, name, displayOffset } = this.selectedTimeZone;
    const tooltipTitle = (!abbv || abbv === NOT_FOUND) ? '' : `${name} - ${abbv} (UTC${displayOffset})`;

    return (
      <Tooltip
        title={tooltipTitle}
        placement='right-end'
        open={displayTimeZoneInfo && !anchorEl}
        classes={{ tooltip:classes && classes.customTooltip }}
      >
        <MuiThemeProvider theme={customMenuItem}>
          <div
            className={styles.timeZoneSelector}
            onMouseEnter={this.handleMouseEnter}
            onMouseLeave={this.handleMouseLeave}
            onBlur={this.handleMouseLeave}
          >
            {displayIcon && <AccessTime className={styles.icon}/>}
            {this.drawSearchDisplay()}
          </div>
        </MuiThemeProvider>
      </Tooltip>
    );
  }
}

import { connect } from 'react-redux';
import { compose } from 'redux';
import {
  namespace as timeZoneNamespace,
  loadTimeZones,
  Status as timeZoneStatus
} from '../../state/modules/app/timeZone/actions';


export default connect(
  state => {
    const timeZoneLoadStatus = get(state, [timeZoneNamespace, 'status']);
    const timeZones = get(state, [timeZoneNamespace, 'timeZones'], []);
    const popularTimeZones = timeZones.reduce((accumulator, currentValue) => {
      if (currentValue && popularTimeZoneNames.includes(currentValue.name)) {
        accumulator.push(currentValue);
      }
      return accumulator;
    }, []);

    return {
      timeZones: orderBy(timeZones, 'name', 'asc'),
      popularTimeZones: orderBy(popularTimeZones, 'name', 'asc'),
      timeZoneLoadStatus
    };
  },
  dispatch => ({
    loadTimeZones: compose(
      dispatch,
      loadTimeZones
    )
  })
)(withStyles(muiStyles)(TimeZoneSelector));
